import { memo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTranslation } from 'react-i18next';
import { DEFAULT_API_SERVICE_FILTERS, PAGE_SIZE_OPTIONS } from '../utils/apiServiceUtils';
import { K, apiLabel, getAdapterTypeLabel } from '../apiServiceI18n';

const panelSx = {
  border: '1px solid #d5d9e0',
  borderRadius: 2,
  bgcolor: '#f8f9fc',
  overflow: 'hidden',
};

const filtersHeadingSx = {
  py: 1,
  fontWeight: 700,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  color: '#5f6878',
};

function ApiServiceSearchPanel({
  filterOptions,
  showAccountFilter,
  accounts = [],
  onApply,
  onClear,
  hasActiveFilters,
}) {
  const { t } = useTranslation();
  const [draftFilters, setDraftFilters] = useState(DEFAULT_API_SERVICE_FILTERS);

  const setField = (key, value) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => onApply(draftFilters);

  const handleClear = () => {
    setDraftFilters(DEFAULT_API_SERVICE_FILTERS);
    onClear();
  };

  return (
    <Box sx={panelSx}>
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1.5, bgcolor: '#f0f2f6' }}>
        <Typography sx={filtersHeadingSx}>{t(K.API_SVC_FILTERS)}</Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.25,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" spacing={1.25} sx={{ flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
          {showAccountFilter && (
            <FormControl size="small" sx={{ minWidth: 160, bgcolor: 'white' }}>
              <InputLabel>{apiLabel(K.ACCOUNT)}</InputLabel>
              <Select
                label={apiLabel(K.ACCOUNT)}
                value={draftFilters.accountFilter ?? 'ALL'}
                onChange={(e) => setField('accountFilter', e.target.value)}
              >
                <MenuItem value="ALL">{t(K.ALL)}</MenuItem>
                {accounts.map((acc) => (
                  <MenuItem key={acc} value={acc}>
                    {acc}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            size="small"
            label={apiLabel(K.LBL_NAME)}
            value={draftFilters.name}
            onChange={(e) => setField('name', e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleApply()}
            sx={{ minWidth: 160, bgcolor: 'white' }}
          />

          <FormControl size="small" sx={{ minWidth: 180, bgcolor: 'white' }}>
            <InputLabel>{apiLabel(K.WORKFLOW_GROUP)}</InputLabel>
            <Select
              label={apiLabel(K.WORKFLOW_GROUP)}
              value={draftFilters.workflowGroup}
              onChange={(e) => setField('workflowGroup', e.target.value)}
            >
              <MenuItem value="ALL">{t(K.ALL)}</MenuItem>
              {filterOptions.workflowGroups.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180, bgcolor: 'white' }}>
            <InputLabel>{apiLabel(K.WORKFLOW)}</InputLabel>
            <Select
              label={apiLabel(K.WORKFLOW)}
              value={draftFilters.workflow}
              onChange={(e) => setField('workflow', e.target.value)}
            >
              <MenuItem value="">{t(K.ALL)}</MenuItem>
              {filterOptions.workflows.map((w) => (
                <MenuItem key={w} value={w}>
                  {w}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140, bgcolor: 'white' }}>
            <InputLabel>{apiLabel(K.LBL_STATUS)}</InputLabel>
            <Select
              label={apiLabel(K.LBL_STATUS)}
              value={draftFilters.status}
              onChange={(e) => setField('status', e.target.value)}
            >
              <MenuItem value="ALL">{t(K.ALL)}</MenuItem>
              <MenuItem value="enabled">{t(K.API_SVC_CHIP_ENABLED)}</MenuItem>
              <MenuItem value="disabled">{t(K.API_SVC_CHIP_DISABLED)}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200, bgcolor: 'white' }}>
            <InputLabel>{t(K.API_SVC_COL_ADAPTER)}</InputLabel>
            <Select
              label={t(K.API_SVC_COL_ADAPTER)}
              value={draftFilters.adapter}
              onChange={(e) => setField('adapter', e.target.value)}
            >
              <MenuItem value="ALL">{t(K.ALL)}</MenuItem>
              {filterOptions.adapters.map((a) => (
                <MenuItem key={a} value={a}>
                  {getAdapterTypeLabel(a, t)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120, bgcolor: 'white' }}>
            <InputLabel>{t(K.API_SVC_SEARCH_LIMIT)}</InputLabel>
            <Select
              label={t(K.API_SVC_SEARCH_LIMIT)}
              value={draftFilters.pageSize}
              onChange={(e) => setField('pageSize', Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          </Stack>

          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', flexShrink: 0 }}>
            {hasActiveFilters && (
              <Button variant="outlined" size="small" onClick={handleClear}>
                {t(K.API_SVC_CLEAR_FILTER)}
              </Button>
            )}
            <Button variant="contained" size="small" onClick={handleApply} startIcon={<FilterListIcon />}>
              {t(K.API_SVC_FILTER_LIST)}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

export default memo(ApiServiceSearchPanel);
